import { findProductByPartNo, isHardIoCapabilityEligible } from "@automatry/catalog";
import { validateControllerPlan } from "@automatry/rules";
import { EngineToolRequestSchema, EngineToolResponseSchema, IdSchema, NonEmptyStringSchema, PointDemandSchema, PointVectorSchema, RuleTraceRefSchema, SourceRefSchema, SpecDecisionProposalKindSchema, SpecSnapshotSchema, ToolCallTraceSchema, VerifierResultSchema, ZoneSlotAssignmentSchema } from "@automatry/schemas";
import { z } from "zod";
import { getCatalogProducts } from "../controllerAllocation.js";
import { deterministicHash } from "../hash.js";
import { buildApartmentPointLedger, buildControllerPointLedger } from "../pointLedger.js";
import { ENGINE_TIMESTAMP } from "../strategyContext.js";
import { validateSnapshot } from "../validate.js";
export const ENGINE_TOOL_NAMES = {
    pointCapacity: "spec_engine.point_capacity.check",
    snapshotValidation: "spec_engine.snapshot_validation.check",
    catalogCapability: "spec_engine.catalog_capability.check",
    ruleInvariants: "spec_engine.rule_invariants.check"
};
const PointCapacityControllerInputSchema = z
    .object({
    id: IdSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    capacity: PointVectorSchema,
    zoneSlots: z.array(ZoneSlotAssignmentSchema),
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default(["tool-point-capacity"])
})
    .strict();
const PointCapacityToolInputSchema = z
    .object({
    proposalId: IdSchema.optional(),
    proposalKind: SpecDecisionProposalKindSchema.default("controller_allocation"),
    apartmentTypeId: IdSchema,
    pointDemands: z.array(PointDemandSchema),
    controllers: z.array(PointCapacityControllerInputSchema),
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default(["tool-point-capacity"])
})
    .strict();
const SnapshotValidationToolInputSchema = z
    .object({
    proposalId: IdSchema.optional(),
    proposalKind: SpecDecisionProposalKindSchema.default("composed"),
    snapshot: SpecSnapshotSchema
})
    .strict();
const CatalogCapabilityToolInputSchema = z
    .object({
    proposalId: IdSchema.optional(),
    proposalKind: SpecDecisionProposalKindSchema.default("composed"),
    productPartNo: NonEmptyStringSchema,
    required: z
        .object({
        hardIo: z.boolean().optional(),
        points: PointVectorSchema.optional(),
        roles: z.array(NonEmptyStringSchema).optional(),
        protocols: z.array(NonEmptyStringSchema).optional()
    })
        .strict()
        .optional(),
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default(["tool-catalog-capability"])
})
    .strict();
const RuleInvariantZoneSlotSchema = z
    .object({
    slot: z.number().int().positive(),
    role: z.enum(["primary", "secondary", "overflow"]),
    logicalZoneId: IdSchema,
    zoneKind: z.enum(["fcu", "wufh", "eufh", "heat_only"]),
    combinedLoadZoneIds: z.array(IdSchema).default([]),
    notes: NonEmptyStringSchema.optional()
})
    .strict();
const RuleInvariantControllerSchema = z
    .object({
    id: IdSchema,
    productPartNo: z.enum(["TP-C-710R-IP", "MZ-503"]),
    role: z.enum(["fcu_primary", "heat_only_710", "mz_overflow"]),
    physicalLocation: z.enum(["fcu_mounted", "central_panel"]),
    zoneSlots: z.array(RuleInvariantZoneSlotSchema)
})
    .strict();
const RuleInvariantToolInputSchema = z
    .object({
    proposalId: IdSchema.optional(),
    proposalKind: SpecDecisionProposalKindSchema.default("controller_allocation"),
    controllers: z.array(RuleInvariantControllerSchema),
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default(["tool-rule-invariants"])
})
    .strict();
const engineToolRegistry = {
    [ENGINE_TOOL_NAMES.pointCapacity]: {
        toolName: ENGINE_TOOL_NAMES.pointCapacity,
        inputSchema: PointCapacityToolInputSchema,
        handle: (request, input) => handlePointCapacity(request, input)
    },
    [ENGINE_TOOL_NAMES.snapshotValidation]: {
        toolName: ENGINE_TOOL_NAMES.snapshotValidation,
        inputSchema: SnapshotValidationToolInputSchema,
        handle: (request, input, context) => handleSnapshotValidation(request, input, context)
    },
    [ENGINE_TOOL_NAMES.catalogCapability]: {
        toolName: ENGINE_TOOL_NAMES.catalogCapability,
        inputSchema: CatalogCapabilityToolInputSchema,
        handle: (request, input, context) => handleCatalogCapability(request, input, context)
    },
    [ENGINE_TOOL_NAMES.ruleInvariants]: {
        toolName: ENGINE_TOOL_NAMES.ruleInvariants,
        inputSchema: RuleInvariantToolInputSchema,
        handle: (request, input) => handleRuleInvariants(request, input)
    }
};
export const listEngineTools = () => Object.keys(engineToolRegistry);
export function dispatchEngineTool(request, context) {
    const startedAt = context.now?.() ?? ENGINE_TIMESTAMP;
    const requestHash = deterministicHash(request);
    try {
        const parsedRequest = EngineToolRequestSchema.parse(request);
        const handler = engineToolRegistry[parsedRequest.toolName];
        if (!handler) {
            return withTrace({
                request: parsedRequest,
                startedAt,
                requestHash,
                response: makeFailedResponse({
                    requestId: parsedRequest.id,
                    error: `Unknown engine tool: ${parsedRequest.toolName}`,
                    validationErrors: [`Unknown engine tool: ${parsedRequest.toolName}`],
                    respondedAt: startedAt
                })
            });
        }
        const input = handler.inputSchema.parse(parsedRequest.input);
        const result = handler.handle(parsedRequest, input, context);
        return withTrace({
            request: parsedRequest,
            startedAt,
            requestHash,
            response: result.response,
            ...(result.verifierResult ? { verifierResult: result.verifierResult } : {})
        });
    }
    catch (error) {
        const fallbackRequestId = typeof request === "object" && "id" in request && typeof request.id === "string"
            ? request.id
            : "invalid-engine-tool-request";
        const validationErrors = zodMessages(error);
        const response = makeFailedResponse({
            requestId: fallbackRequestId,
            error: error instanceof Error
                ? error.message
                : "Engine tool request failed validation.",
            validationErrors,
            respondedAt: startedAt
        });
        return withTrace({
            request,
            startedAt,
            requestHash,
            response
        });
    }
}
function handlePointCapacity(request, input) {
    const controllerLedgers = input.controllers.map((controller) => buildControllerPointLedger({
        controllerId: controller.id,
        pointDemands: input.pointDemands,
        zoneSlots: cleanZoneSlots(controller.zoneSlots),
        capacity: controller.capacity,
        sourceRefs: controller.sourceRefs.length > 0 ? controller.sourceRefs : input.sourceRefs,
        ruleTraceRefs: controller.ruleTraceRefs.length > 0
            ? controller.ruleTraceRefs
            : input.ruleTraceRefs
    }));
    const apartmentLedger = buildApartmentPointLedger({
        apartmentTypeId: input.apartmentTypeId,
        pointDemands: input.pointDemands,
        controllers: input.controllers.map((controller) => ({
            capacity: controller.capacity
        })),
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    });
    const ledgers = [...controllerLedgers, apartmentLedger];
    const findings = ledgers.flatMap((ledger) => ledger.violations.map((violation) => makeVerifierFinding({
        id: `finding-${request.id}-${ledger.id}-${violation.pointKind}`,
        kind: "capacity_over_subscription",
        severity: "blocker",
        code: ledger.id === apartmentLedger.id
            ? "io.apartment_over_capacity"
            : "io.controller_over_capacity",
        subjectRef: ledger.id,
        message: violation.message,
        details: {
            pointKind: violation.pointKind,
            required: violation.required,
            available: violation.available,
            ledgerRequired: ledger.required,
            ledgerAvailable: ledger.available,
            ledgerSpare: ledger.spare
        },
        sourceRefs: ledger.sourceRefs,
        ruleTraceRefs: ledger.ruleTraceRefs
    })));
    const verifierResult = makeVerifierResult({
        id: `verifier-${request.id}-point-capacity`,
        proposalId: input.proposalId ?? request.id,
        proposalKind: input.proposalKind,
        checkKind: "point_capacity",
        findings,
        message: findings.length > 0
            ? "Point capacity verifier found over-capacity hard-IO ledgers."
            : "Point capacity verifier accepted the controller and apartment ledgers.",
        toolRequestId: request.id
    });
    return {
        response: makeSucceededResponse({
            requestId: request.id,
            output: {
                verifierResult,
                summary: summarizeLedgers(ledgers),
                controllerLedgers,
                apartmentLedger
            },
            respondedAt: ENGINE_TIMESTAMP
        }),
        verifierResult
    };
}
function handleSnapshotValidation(request, input, context) {
    const validation = validateSnapshot(input.snapshot, context.catalog);
    const findings = validation.findings.map((finding, index) => verifierFindingFromValidationFinding({
        requestId: request.id,
        finding,
        index
    }));
    const verifierResult = makeVerifierResult({
        id: `verifier-${request.id}-snapshot-validation`,
        proposalId: input.proposalId ?? request.id,
        proposalKind: input.proposalKind,
        checkKind: "rule_invariant",
        findings,
        message: findings.length > 0
            ? "Snapshot validation found blocking rule, IO, product, or trace findings."
            : "Snapshot validation accepted the proposed snapshot.",
        toolRequestId: request.id
    });
    return {
        response: makeSucceededResponse({
            requestId: request.id,
            output: {
                verifierResult,
                validation
            },
            respondedAt: ENGINE_TIMESTAMP
        }),
        verifierResult
    };
}
function handleCatalogCapability(request, input, context) {
    const product = findProductByPartNo(getCatalogProducts(context.catalog), input.productPartNo);
    const findings = [];
    if (!product) {
        findings.push(makeVerifierFinding({
            id: `finding-${request.id}-unknown-product`,
            kind: "unknown_product",
            severity: "blocker",
            code: "catalog.unknown_product",
            subjectRef: input.productPartNo,
            message: `${input.productPartNo} is absent from the supplied catalog products.`,
            details: {
                productPartNo: input.productPartNo
            },
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: input.ruleTraceRefs
        }));
    }
    else {
        findings.push(...catalogCapabilityFindings({ request, input, product }));
    }
    const verifierResult = makeVerifierResult({
        id: `verifier-${request.id}-catalog-capability`,
        proposalId: input.proposalId ?? request.id,
        proposalKind: input.proposalKind,
        checkKind: "catalog_review",
        findings,
        message: findings.length > 0
            ? "Catalog capability verifier found product/capability violations."
            : `${input.productPartNo} exists and satisfies the requested catalog capability checks.`,
        toolRequestId: request.id
    });
    return {
        response: makeSucceededResponse({
            requestId: request.id,
            output: {
                verifierResult,
                product: product ? productSummary(product) : null
            },
            respondedAt: ENGINE_TIMESTAMP
        }),
        verifierResult
    };
}
function handleRuleInvariants(request, input) {
    const plan = {
        controllers: input.controllers.map((controller) => ({
            id: controller.id,
            productPartNo: controller.productPartNo,
            role: controller.role,
            physicalLocation: controller.physicalLocation,
            zoneSlots: controller.zoneSlots.map((slot) => ({
                slot: slot.slot,
                role: slot.role,
                zoneId: slot.logicalZoneId,
                zoneKind: slot.zoneKind,
                combinedLoadZoneIds: slot.combinedLoadZoneIds,
                notes: slot.notes ?? ""
            }))
        })),
        unassignedZones: [],
        traces: input.ruleTraceRefs.map((ruleTraceRef) => ({
            id: ruleTraceRef,
            ruleCardId: ruleTraceRef,
            ruleSetId: "baseline-2026-01",
            summary: `Injected controller plan trace ${ruleTraceRef}.`,
            sourceRefs: input.sourceRefs,
            createdAt: ENGINE_TIMESTAMP
        }))
    };
    const constraints = validateControllerPlan(plan);
    const findings = constraints
        .filter((constraint) => constraint.status === "fail")
        .map((constraint) => verifierFindingFromConstraint({
        requestId: request.id,
        constraint,
        sourceRefs: constraint.sourceRefs.length > 0 ? constraint.sourceRefs : input.sourceRefs,
        ruleTraceRefs: constraint.ruleTraceRefs.length > 0
            ? constraint.ruleTraceRefs
            : input.ruleTraceRefs
    }));
    const verifierResult = makeVerifierResult({
        id: `verifier-${request.id}-rule-invariants`,
        proposalId: input.proposalId ?? request.id,
        proposalKind: input.proposalKind,
        checkKind: "rule_invariant",
        findings,
        message: findings.length > 0
            ? "Rule invariant verifier found controller-packing violations."
            : "Rule invariant verifier accepted the controller-packing plan.",
        toolRequestId: request.id
    });
    return {
        response: makeSucceededResponse({
            requestId: request.id,
            output: {
                verifierResult,
                constraints
            },
            respondedAt: ENGINE_TIMESTAMP
        }),
        verifierResult
    };
}
function catalogCapabilityFindings(input) {
    const findings = [];
    const required = input.input.required;
    if (required?.hardIo === true && !isHardIoCapabilityEligible(input.product)) {
        findings.push(makeVerifierFinding({
            id: `finding-${input.request.id}-unreviewed-product`,
            kind: "unreviewed_product",
            severity: "blocker",
            code: "catalog.unreviewed_product",
            subjectRef: input.product.partNo,
            message: `${input.product.partNo} is not reviewed for hard-IO roles.`,
            details: {
                capabilityStatus: input.product.capabilityStatus,
                sourceStatus: input.product.sourceStatus
            },
            sourceRefs: sourceRefsForProduct(input.input, input.product),
            ruleTraceRefs: input.input.ruleTraceRefs
        }));
    }
    if (required?.points) {
        for (const pointKind of ["ui", "di", "ao", "do", "relay"]) {
            if (required.points[pointKind] > input.product.capabilities.points[pointKind]) {
                findings.push(makeVerifierFinding({
                    id: `finding-${input.request.id}-insufficient-${pointKind}`,
                    kind: "capacity_over_subscription",
                    severity: "blocker",
                    code: "catalog.insufficient_points",
                    subjectRef: input.product.partNo,
                    message: `${input.product.partNo} ${pointKind.toUpperCase()} capacity ${input.product.capabilities.points[pointKind]} is below required ${required.points[pointKind]}.`,
                    details: {
                        pointKind,
                        required: required.points[pointKind],
                        available: input.product.capabilities.points[pointKind]
                    },
                    sourceRefs: sourceRefsForProduct(input.input, input.product),
                    ruleTraceRefs: input.input.ruleTraceRefs
                }));
            }
        }
    }
    for (const role of required?.roles ?? []) {
        if (!input.product.capabilities.roles.includes(role)) {
            findings.push(makeVerifierFinding({
                id: `finding-${input.request.id}-missing-role-${safeId(role)}`,
                kind: "rule_invariant_breach",
                severity: "error",
                code: "catalog.missing_role",
                subjectRef: input.product.partNo,
                message: `${input.product.partNo} does not advertise required role ${role}.`,
                details: {
                    role,
                    roles: input.product.capabilities.roles
                },
                sourceRefs: sourceRefsForProduct(input.input, input.product),
                ruleTraceRefs: input.input.ruleTraceRefs
            }));
        }
    }
    for (const protocol of required?.protocols ?? []) {
        if (!input.product.capabilities.protocols.includes(protocol)) {
            findings.push(makeVerifierFinding({
                id: `finding-${input.request.id}-missing-protocol-${safeId(protocol)}`,
                kind: "rule_invariant_breach",
                severity: "error",
                code: "catalog.missing_protocol",
                subjectRef: input.product.partNo,
                message: `${input.product.partNo} does not advertise required protocol ${protocol}.`,
                details: {
                    protocol,
                    protocols: input.product.capabilities.protocols
                },
                sourceRefs: sourceRefsForProduct(input.input, input.product),
                ruleTraceRefs: input.input.ruleTraceRefs
            }));
        }
    }
    return findings;
}
function verifierFindingFromValidationFinding(input) {
    return makeVerifierFinding({
        id: `finding-${input.requestId}-validation-${input.index + 1}`,
        kind: validationFindingKind(input.finding),
        severity: input.finding.severity === "error" ? "blocker" : input.finding.severity,
        code: input.finding.code,
        ...(input.finding.subjectRef ? { subjectRef: input.finding.subjectRef } : {}),
        message: input.finding.message,
        details: {
            validationCode: input.finding.code
        },
        sourceRefs: input.finding.sourceRefs,
        ruleTraceRefs: input.finding.ruleTraceRefs
    });
}
function verifierFindingFromConstraint(input) {
    return makeVerifierFinding({
        id: `finding-${input.requestId}-${input.constraint.id}`,
        kind: "rule_invariant_breach",
        severity: "blocker",
        code: input.constraint.constraintKey,
        subjectRef: input.constraint.subjectRef,
        message: input.constraint.message,
        details: input.constraint.details,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    });
}
function validationFindingKind(finding) {
    if (finding.code.startsWith("io.")) {
        return "capacity_over_subscription";
    }
    if (finding.code.includes("unknown")) {
        return "unknown_product";
    }
    if (finding.code.includes("unreviewed")) {
        return "unreviewed_product";
    }
    if (finding.code.startsWith("trace.")) {
        return "missing_requirement_trace";
    }
    if (finding.code.startsWith("risk.") || finding.code.startsWith("schema.")) {
        return "rule_invariant_breach";
    }
    return "other";
}
function makeVerifierResult(input) {
    const severity = maxSeverity(input.findings);
    return VerifierResultSchema.parse({
        id: input.id,
        proposalId: input.proposalId,
        proposalKind: input.proposalKind,
        status: input.findings.length > 0 ? "violation" : "ok",
        checkKind: input.checkKind,
        severity,
        message: input.message,
        findings: input.findings,
        toolRequestId: input.toolRequestId,
        checkedAt: ENGINE_TIMESTAMP
    });
}
function makeVerifierFinding(input) {
    return {
        id: input.id,
        kind: input.kind,
        severity: input.severity,
        code: input.code,
        ...(input.subjectRef ? { subjectRef: input.subjectRef } : {}),
        message: input.message,
        details: input.details,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    };
}
function makeSucceededResponse(input) {
    return EngineToolResponseSchema.parse({
        requestId: input.requestId,
        status: "succeeded",
        output: input.output,
        validationErrors: [],
        respondedAt: input.respondedAt
    });
}
function makeFailedResponse(input) {
    return EngineToolResponseSchema.parse({
        requestId: input.requestId,
        status: "failed",
        validationErrors: input.validationErrors,
        error: input.error,
        respondedAt: input.respondedAt
    });
}
function withTrace(input) {
    const responseHash = deterministicHash(input.response);
    const request = typeof input.request === "object" && input.request !== null
        ? input.request
        : { value: input.request };
    const toolName = "toolName" in request && typeof request.toolName === "string"
        ? request.toolName
        : "unknown";
    const id = "id" in request && typeof request.id === "string"
        ? `tool-call-${request.id}`
        : `tool-call-${input.response.requestId}`;
    const trace = ToolCallTraceSchema.parse({
        id,
        toolName,
        requestHash: input.requestHash,
        responseHash,
        input: request,
        output: input.response.output,
        status: input.response.status === "succeeded" ? "succeeded" : "failed",
        ...(input.response.error ? { error: input.response.error } : {}),
        startedAt: input.startedAt,
        completedAt: input.response.respondedAt
    });
    return input.verifierResult
        ? { response: input.response, trace, verifierResult: input.verifierResult }
        : { response: input.response, trace };
}
function zodMessages(error) {
    if (isZodError(error)) {
        return error.issues.map((issue) => {
            const path = issue.path.length > 0 ? `${issue.path.join(".")}: ` : "";
            return `${path}${issue.message}`;
        });
    }
    return [
        error instanceof Error ? error.message : "Engine tool request failed validation."
    ];
}
function isZodError(error) {
    return (typeof error === "object" &&
        error !== null &&
        "issues" in error &&
        Array.isArray(error.issues));
}
function maxSeverity(findings) {
    const order = {
        info: 0,
        warning: 1,
        error: 2,
        blocker: 3
    };
    return findings.reduce((max, finding) => {
        const severity = finding.severity;
        return order[severity] > order[max] ? severity : max;
    }, "info");
}
function cleanZoneSlots(zoneSlots) {
    return zoneSlots.map((slot) => ({
        slot: slot.slot,
        logicalZoneId: slot.logicalZoneId,
        role: slot.role,
        ...(slot.notes ? { notes: slot.notes } : {})
    }));
}
function summarizeLedgers(ledgers) {
    return {
        ledgers: ledgers.map((ledger) => ({
            id: ledger.id,
            required: ledger.required,
            available: ledger.available,
            spare: ledger.spare,
            violationCount: ledger.violations.length
        })),
        violationCount: ledgers.reduce((total, ledger) => total + ledger.violations.length, 0)
    };
}
function sourceRefsForProduct(input, product) {
    return input.sourceRefs.length > 0 ? input.sourceRefs : product.sourceRefs;
}
function productSummary(product) {
    return {
        id: product.id,
        partNo: product.partNo,
        aliases: product.aliases,
        family: product.family,
        supplySource: product.supplySource,
        sourceStatus: product.sourceStatus,
        capabilityStatus: product.capabilityStatus,
        capabilities: product.capabilities
    };
}
function safeId(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}
//# sourceMappingURL=index.js.map